
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import Button from "@mui/material/Button";
import Input from "@mui/material/Input";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
    Plus,
    Search,
    Filter,
    MoreVertical,
    Edit,
    Trash2,
    Eye,
    FileText,
    Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";
// import { useToast } from "@/hooks/use-toast";

export default function FormsList() {
    const navigate = useNavigate();

    const [location, setLocation] = useLocation();
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteForm, setDeleteForm] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuFormId, setMenuFormId] = useState(null);
    // const { toast } = useToast();
    const queryClient = useQueryClient();

    //       const { data: forms, isLoading: formsLoading } = useQuery({
    //     queryKey: [`/api/forms?userId=${DEMO_USER_ID}`],
    //   });

    // Fetch forms
    const { data: forms = [], isLoading } = useQuery({
        queryKey: [`${process.env.REACT_APP_API_URL}/api/admin/dynamicform/forms`],
        queryFn: () => fetch(`${process.env.REACT_APP_API_URL}/api/admin/dynamicform/forms`).then(res => res.json()),
    });

   //console.log(forms);


    // Delete form mutation
    const deleteMutation = useMutation({
        mutationFn: async (formId) => {
            const response = await fetch(`/api/forms/${formId}`, {
                method: "DELETE",
            });
            if (!response.ok) throw new Error("Failed to delete form");
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/forms"] });
            // toast({
            //     title: "Form deleted",
            //     description: "The form has been successfully deleted.",
            // });
            setDeleteForm(null);
        },
        onError: () => {
            // toast({
            //     title: "Error",
            //     description: "Failed to delete the form. Please try again.",
            //     variant: "destructive",
            // });
        },
    });
   //console.log(forms);

    const filteredForms = forms?.filter(form =>
        form.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleEdit = (formId) => {
        navigate(`/forms/${formId}`);
    };

    const handleView = (formId) => {
       //console.log("Viewing form:", formId);
        navigate(`/form-preview/${formId}`);
    };

    const handleDelete = (form) => {
        setDeleteForm(form);
        handleMenuClose();
    };

    const confirmDelete = () => {
        if (deleteForm) {
            deleteMutation.mutate(deleteForm.id);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getFieldCount = (fields) => {
        if (typeof fields === "string") {
            try {
                return JSON.parse(fields).length;
            } catch {
                return 0;
            }
        }
        return Array.isArray(fields) ? fields.length : 0;
    };

    // Menu handlers
    const handleMenuOpen = (event, formId) => {
        setAnchorEl(event.currentTarget);
        setMenuFormId(formId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuFormId(null);
    };

    // Inline styles for layout (no Tailwind)
    const styles = {
        page: {
            minHeight: "100vh",
            background: "#f7f7f7",
            fontFamily: "sans-serif"
        },
        header: {
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            padding: "0 32px"
        },
        headerInner: {
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "64px"
        },
        logo: {
            width: "32px",
            height: "32px",
            background: "#2563eb",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "16px"
        },
        logoText: {
            color: "#fff",
            fontWeight: "bold",
            fontSize: "16px"
        },
        title: {
            fontSize: "22px",
            fontWeight: "600",
            color: "#222"
        },
        subtitle: {
            fontSize: "14px",
            color: "#888"
        },
        content: {
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "32px"
        },
        searchRow: {
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "16px"
        },
        searchInputWrap: {
            position: "relative",
            flex: 1,
            maxWidth: "350px"
        },
        searchIcon: {
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#bbb",
            width: "18px",
            height: "18px"
        },
        input: {
            paddingLeft: "36px",
            width: "100%"
        },
        grid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "24px"
        },
        card: {
            cursor: "pointer",
            transition: "box-shadow 0.2s",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            borderRadius: "8px"
        },
        cardContent: {
            padding: "24px"
        },
        cardTitle: {
            fontSize: "18px",
            fontWeight: "500",
            color: "#222",
            marginBottom: "4px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        },
        cardDesc: {
            fontSize: "14px",
            color: "#666",
            marginTop: "4px",
            marginBottom: "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
        },
        badge: {
            background: "#e5e7eb",
            color: "#333",
            fontSize: "12px",
            borderRadius: "4px",
            padding: "2px 8px",
            marginRight: "8px"
        },
        date: {
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
            color: "#888"
        },
        actions: {
            marginTop: "16px",
            display: "flex",
            gap: "8px"
        },
        empty: {
            textAlign: "center",
            padding: "48px 0"
        },
        emptyIcon: {
            width: "48px",
            height: "48px",
            color: "#bbb",
            marginBottom: "16px"
        }
    };

    return (
        <div style={styles.page}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerInner}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <div style={styles.logo}>
                            <span style={styles.logoText}>F</span>
                        </div>
                        <div>
                            <div style={styles.title}>My Forms</div>
                            <div style={styles.subtitle}>
                                {forms.length} form{forms.length !== 1 ? 's' : ''}
                            </div>
                        </div>
                    </div>
                    <Link href="/forms/new">
                        <Button variant="contained" style={{ background: "#2563eb", color: "#fff" }}>
                            <Plus style={{ width: 18, height: 18, marginRight: 8 }} />
                            New Form
                        </Button>
                    </Link>
                </div>
            </div>

            <div style={styles.content}>
                <div style={styles.searchRow}>
                    <div style={styles.searchInputWrap}>
                        <Search style={styles.searchIcon} />
                        <Input
                            placeholder="Search forms..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.input}
                            disableUnderline
                        />
                    </div>
                    <Button variant="outlined" size="small">
                        <Filter style={{ width: 18, height: 18, marginRight: 8 }} />
                        Filter
                    </Button>
                </div>

                {isLoading ? (
                    <div style={styles.grid}>
                        {[...Array(6)].map((_, i) => (
                            <Card key={i} style={styles.card}>
                                <CardContent style={styles.cardContent}>
                                    <div style={{ height: 16, background: "#eee", borderRadius: 4, marginBottom: 16 }}></div>
                                    <div style={{ height: 12, background: "#eee", borderRadius: 4, marginBottom: 8 }}></div>
                                    <div style={{ height: 12, background: "#eee", borderRadius: 4, width: "66%" }}></div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredForms.length === 0 ? (
                    <div style={styles.empty}>
                        <FileText style={styles.emptyIcon} />
                        <h3 style={{ fontSize: "18px", fontWeight: "500", color: "#222", marginBottom: "8px" }}>
                            {searchTerm ? "No forms found" : "No forms yet"}
                        </h3>
                        <div style={{ color: "#888", marginBottom: "24px" }}>
                            {searchTerm
                                ? "Try adjusting your search terms"
                                : "Create your first form to get started"}
                        </div>
                        <Link href="/forms/new">
                            <Button variant="contained">
                                <Plus style={{ width: 18, height: 18, marginRight: 8 }} />
                                Create Form
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {filteredForms.map((form) => (
                            <Card key={form._id} style={styles.card}>
                                <CardContent style={styles.cardContent}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={styles.cardTitle}>{form.title}</div>
                                            {form.description && (
                                                <div style={styles.cardDesc}>{form.description}</div>
                                            )}
                                        </div>
                                        <IconButton
                                            onClick={(e) => handleMenuOpen(e, form._id)}
                                            size="small"
                                            style={{ opacity: 0.7 }}
                                        >
                                            <MoreVertical style={{ width: 18, height: 18 }} />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={menuFormId === form._id}
                                            onClose={handleMenuClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <MenuItem onClick={() => { handleView(form); handleMenuClose(); }}>
                                                <Eye style={{ width: 18, height: 18, marginRight: 8 }} />
                                                View
                                            </MenuItem>
                                            <MenuItem onClick={() => { handleEdit(form._id); handleMenuClose(); }}>
                                                <Edit style={{ width: 18, height: 18, marginRight: 8 }} />
                                                Edit
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => handleDelete(form)}
                                                style={{ color: "#e11d48" }}
                                            >
                                                <Trash2 style={{ width: 18, height: 18, marginRight: 8 }} />
                                                Delete
                                            </MenuItem>
                                        </Menu>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#888" }}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {/* <Badge
                                                badgeContent={getFieldCount(form.fields)}
                                                color="secondary"
                                                style={{ marginRight: 8 }}
                                            >
                                                <span style={{ fontSize: "12px" }}>fields</span>
                                            </Badge> */}
                                            <div style={styles.date}>
                                                <Calendar style={{ width: 14, height: 14, marginRight: 4 }} />
                                                {formatDate(form.createdAt)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quick action buttons */}
                                    <div style={styles.actions}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            style={{ flex: 1 }}
                                            onClick={() => handleView(form)}
                                        >
                                            <Eye style={{ width: 14, height: 14, marginRight: 4 }} />
                                            View
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            style={{ flex: 1 }}
                                            onClick={() => handleEdit(form._id)}
                                        >
                                            <Edit style={{ width: 14, height: 14, marginRight: 4 }} />
                                            Edit
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteForm} onClose={() => setDeleteForm(null)}>
                <DialogTitle>Delete Form</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{deleteForm?.title}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteForm(null)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        disabled={deleteMutation.isPending}
                        variant="contained"
                    >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}


// import React, { useState, useRef } from 'react';
// import {
//   Layout,
//   Card,
//   Button,
//   Input,
//   Select,
//   DatePicker,
//   Upload,
//   ColorPicker,
//   Space,
//   Typography,
//   Divider,
//   Modal,
//   Switch,
//   InputNumber,
//   message,
//   Tabs,
//   Tag,
//   Popconfirm
// } from 'antd';
// import {
//   DragOutlined,
//   DeleteOutlined,
//   EditOutlined,
//   EyeOutlined,
//   CopyOutlined,
//   DownloadOutlined,
//   UploadOutlined,
//   SaveOutlined
// } from '@ant-design/icons';

// const { Sider, Content } = Layout;
// const { Title, Text } = Typography;
// const { TextArea } = Input;
// const { Option } = Select;

// // Field Types Configuration
// const FIELD_TYPES = {
//   text: {
//     label: 'Text Input',
//     icon: '📝',
//     defaultProps: { placeholder: 'Enter text', required: false }
//   },
//   number: {
//     label: 'Number Input',
//     icon: '🔢',
//     defaultProps: { placeholder: 'Enter number', min: 0, required: false }
//   },
//   email: {
//     label: 'Email Input',
//     icon: '📧',
//     defaultProps: { placeholder: 'Enter email', required: false }
//   },
//   date: {
//     label: 'Date Picker',
//     icon: '📅',
//     defaultProps: { placeholder: 'Select date', required: false }
//   },
//   select: {
//     label: 'Select Dropdown',
//     icon: '📋',
//     defaultProps: { placeholder: 'Choose option', options: ['Option 1', 'Option 2'], required: false }
//   },
//   textarea: {
//     label: 'Text Area',
//     icon: '📄',
//     defaultProps: { placeholder: 'Enter description', rows: 4, required: false }
//   },
//   file: {
//     label: 'File Upload',
//     icon: '📎',
//     defaultProps: { accept: '*', maxCount: 1, required: false }
//   },
//   color: {
//     label: 'Color Picker',
//     icon: '🎨',
//     defaultProps: { showText: true, required: false }
//   }
// };

// // Event Roles
// const EVENT_ROLES = {
//   visitor: { label: 'Visitor', color: 'blue' },
//   vendor: { label: 'Vendor', color: 'green' },
//   sponsor: { label: 'Sponsor', color: 'gold' },
//   exhibitor: { label: 'Exhibitor', color: 'orange' },
//   retailer: { label: 'Retailer', color: 'purple' },
//   participant: { label: 'Participant', color: 'cyan' }
// };

// // Draggable Field Component
// const DraggableField = ({ fieldType, onDrag }) => {
//   const fieldConfig = FIELD_TYPES[fieldType];
  
//   const handleDragStart = (e) => {
//     e.dataTransfer.setData('fieldType', fieldType);
//     onDrag(fieldType);
//   };

//   return (
//     <Card 
//       size="small" 
//       className="mb-2 cursor-move hover:shadow-md transition-shadow"
//       draggable
//       onDragStart={handleDragStart}
//     >
//       <div className="flex items-center space-x-2">
//         <span className="text-lg">{fieldConfig.icon}</span>
//         <Text strong>{fieldConfig.label}</Text>
//         <DragOutlined className="ml-auto text-gray-400" />
//       </div>
//     </Card>
//   );
// };

// // Form Field Component
// const FormField = ({ field, onEdit, onDelete, onClone }) => {
//   const fieldConfig = FIELD_TYPES[field.type];
  
//   const renderField = () => {
//     switch (field.type) {
//       case 'text':
//       case 'email':
//         return <Input {...field.props} value="" />;
//       case 'number':
//         return <InputNumber {...field.props} value="" style={{ width: '100%' }} />;
//       case 'date':
//         return <DatePicker {...field.props} value="" style={{ width: '100%' }} />;
//       case 'select':
//         return (
//           <Select {...field.props} value="" style={{ width: '100%' }}>
//             {field.props.options?.map((option, idx) => (
//               <Option key={idx} value={option}>{option}</Option>
//             ))}
//           </Select>
//         );
//       case 'textarea':
//         return <TextArea {...field.props} value="" />;
//       case 'file':
//         return (
//           <Upload {...field.props}>
//             <Button icon={<UploadOutlined />}>Upload File</Button>
//           </Upload>
//         );
//       case 'color':
//         return <ColorPicker {...field.props} value="" />;
//       default:
//         return <Input value="" />;
//     }
//   };

//   return (
//     <Card 
//       size="small" 
//       className="mb-3 group hover:shadow-md transition-shadow relative"
//       bodyStyle={{ padding: '16px' }}
//     >
//       <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
//         <Space>
//           <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(field)} />
//           <Button size="small" icon={<CopyOutlined />} onClick={() => onClone(field)} />
//           <Popconfirm
//             title="Delete this field?"
//             onConfirm={() => onDelete(field.id)}
//             okText="Yes"
//             cancelText="No"
//           >
//             <Button size="small" icon={<DeleteOutlined />} danger />
//           </Popconfirm>
//         </Space>
//       </div>
      
//       <div className="mb-3">
//         <label className="flex items-center space-x-2 mb-2 text-sm font-medium">
//           <span>{fieldConfig.icon}</span>
//           <span>{field.label}</span>
//           {field.props.required && <span className="text-red-500">*</span>}
//           {field.roles && field.roles.length > 0 && (
//             <div className="ml-2">
//               {field.roles.map(role => (
//                 <Tag key={role} color={EVENT_ROLES[role].color} size="small">
//                   {EVENT_ROLES[role].label}
//                 </Tag>
//               ))}
//             </div>
//           )}
//         </label>
//         {renderField()}
//       </div>
//     </Card>
//   );
// };

// // Field Editor Modal
// const FieldEditor = ({ visible, field, onSave, onCancel }) => {
//   const [formData, setFormData] = useState({});
//   const [options, setOptions] = useState([]);

//   React.useEffect(() => {
//     if (field) {
//       setFormData({
//         label: field.label,
//         placeholder: field.props.placeholder,
//         required: field.props.required,
//         roles: field.roles || []
//       });
      
//       if (field.props.options) {
//         setOptions(field.props.options.map((opt, idx) => ({ key: idx, value: opt })));
//       }
//     }
//   }, [field]);

//   const handleSave = () => {
//     const updatedField = {
//       ...field,
//       label: formData.label,
//       props: {
//         ...field.props,
//         placeholder: formData.placeholder,
//         required: formData.required,
//         ...(field.type === 'select' && { options: options.map(opt => opt.value) })
//       },
//       roles: formData.roles
//     };
//     onSave(updatedField);
//   };

//   const addOption = () => {
//     const newOption = { key: Date.now(), value: `Option ${options.length + 1}` };
//     setOptions([...options, newOption]);
//   };

//   const removeOption = (key) => {
//     setOptions(options.filter(opt => opt.key !== key));
//   };

//   const updateOption = (key, value) => {
//     setOptions(options.map(opt => opt.key === key ? { ...opt, value } : opt));
//   };

//   return (
//     <Modal
//       title="Edit Field"
//       open={visible}
//       onOk={handleSave}
//       onCancel={onCancel}
//       width={600}
//     >
//       <div className="space-y-4">
//         <div>
//           <label className="block text-sm font-medium mb-1">Field Label</label>
//           <Input 
//             value={formData.label || ''}
//             onChange={(e) => setFormData({...formData, label: e.target.value})}
//           />
//         </div>
        
//         <div>
//           <label className="block text-sm font-medium mb-1">Placeholder</label>
//           <Input 
//             value={formData.placeholder || ''}
//             onChange={(e) => setFormData({...formData, placeholder: e.target.value})}
//           />
//         </div>
        
//         <div className="flex items-center space-x-2">
//           <Switch 
//             checked={formData.required}
//             onChange={(checked) => setFormData({...formData, required: checked})}
//           />
//           <label className="text-sm font-medium">Required Field</label>
//         </div>
        
//         <div>
//           <label className="block text-sm font-medium mb-1">Visible for Roles</label>
//           <Select 
//             mode="multiple" 
//             placeholder="Select roles (leave empty for all)"
//             value={formData.roles || []}
//             onChange={(roles) => setFormData({...formData, roles})}
//             style={{ width: '100%' }}
//           >
//             {Object.entries(EVENT_ROLES).map(([key, role]) => (
//               <Option key={key} value={key}>{role.label}</Option>
//             ))}
//           </Select>
//         </div>

//         {field?.type === 'select' && (
//           <div>
//             <label className="block text-sm font-medium mb-2">Options</label>
//             <div className="space-y-2">
//               {options.map((option) => (
//                 <div key={option.key} className="flex items-center space-x-2">
//                   <Input
//                     value={option.value}
//                     onChange={(e) => updateOption(option.key, e.target.value)}
//                     placeholder="Option value"
//                   />
//                   <Button 
//                     type="text" 
//                     danger 
//                     icon={<DeleteOutlined />}
//                     onClick={() => removeOption(option.key)}
//                   />
//                 </div>
//               ))}
//               <Button type="dashed" onClick={addOption} className="w-full">
//                 Add Option
//               </Button>
//             </div>
//           </div>
//         )}
//       </div>
//     </Modal>
//   );
// };

// // Form Renderer Component
// const FormRenderer = ({ schema, selectedRole }) => {
//   const [formData, setFormData] = useState({});
//   const [submittedData, setSubmittedData] = useState({});

//   const handleSubmit = () => {
//     console.log('Form submitted:', formData);
//     setSubmittedData(formData);
//     message.success('Form submitted successfully!');
//   };

//   const handleFieldChange = (fieldId, value) => {
//     setFormData(prev => ({
//       ...prev,
//       [fieldId]: value
//     }));
//   };

//   const filteredFields = schema.fields.filter(field => 
//     !field.roles || field.roles.length === 0 || field.roles.includes(selectedRole)
//   );

//   const renderFormField = (field) => {
//     const fieldConfig = FIELD_TYPES[field.type];
//     const value = formData[field.id] || '';

//     const commonProps = {
//       ...field.props,
//       value,
//       onChange: (e) => {
//         const val = e?.target ? e.target.value : e;
//         handleFieldChange(field.id, val);
//       }
//     };

//     switch (field.type) {
//       case 'text':
//       case 'email':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <Input {...commonProps} />
//           </div>
//         );
//       case 'number':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <InputNumber 
//               {...field.props}
//               value={value}
//               onChange={(val) => handleFieldChange(field.id, val)}
//               style={{ width: '100%' }} 
//             />
//           </div>
//         );
//       case 'date':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <DatePicker 
//               {...field.props}
//               value={value}
//               onChange={(date) => handleFieldChange(field.id, date)}
//               style={{ width: '100%' }} 
//             />
//           </div>
//         );
//       case 'select':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <Select 
//               {...field.props}
//               value={value}
//               onChange={(val) => handleFieldChange(field.id, val)}
//               style={{ width: '100%' }}
//             >
//               {field.props.options?.map((option, idx) => (
//                 <Option key={idx} value={option}>{option}</Option>
//               ))}
//             </Select>
//           </div>
//         );
//       case 'textarea':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <TextArea {...commonProps} />
//           </div>
//         );
//       case 'file':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <Upload 
//               {...field.props}
//               onChange={(info) => handleFieldChange(field.id, info.fileList)}
//             >
//               <Button icon={<UploadOutlined />}>Upload File</Button>
//             </Upload>
//           </div>
//         );
//       case 'color':
//         return (
//           <div key={field.id} className="mb-4">
//             <label className="block text-sm font-medium mb-2">
//               {field.label} {field.props.required && <span className="text-red-500">*</span>}
//             </label>
//             <ColorPicker 
//               {...field.props}
//               value={value}
//               onChange={(color) => handleFieldChange(field.id, color)}
//             />
//           </div>
//         );
//       default:
//         return null;
//     }
//   };

//   return (
//     <div className="p-6">
//       <div className="mb-6">
//         <Title level={3}>{schema.name}</Title>
//         <Text type="secondary">{schema.description}</Text>
//         <div className="mt-2">
//           <Tag color={EVENT_ROLES[selectedRole].color}>
//             {EVENT_ROLES[selectedRole].label}
//           </Tag>
//         </div>
//       </div>

//       <div className="max-w-2xl">
//         {filteredFields.map(renderFormField)}
        
//         <div className="mt-6">
//           <Button type="primary" size="large" onClick={handleSubmit}>
//             Submit Registration
//           </Button>
//         </div>
//       </div>

//       {Object.keys(submittedData).length > 0 && (
//         <Card title="Submitted Data" className="mt-6">
//           <pre>{JSON.stringify(submittedData, null, 2)}</pre>
//         </Card>
//       )}
//     </div>
//   );
// };

// // Main Form Builder Component
// const FormList = () => {
//   const [activeTab, setActiveTab] = useState('builder');
//   const [selectedRole, setSelectedRole] = useState('visitor');
//   const [schema, setSchema] = useState({
//     id: 'form_1',
//     name: 'Event Registration Form',
//     description: 'Complete your event registration',
//     fields: []
//   });
  
//   const [editingField, setEditingField] = useState(null);
//   const [editorVisible, setEditorVisible] = useState(false);
//   const dropZoneRef = useRef(null);

//   const handleDragOver = (e) => {
//     e.preventDefault();
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     const fieldType = e.dataTransfer.getData('fieldType');
//     addField(fieldType);
//   };

//   const addField = (fieldType) => {
//     const fieldConfig = FIELD_TYPES[fieldType];
//     const newField = {
//       id: `field_${Date.now()}`,
//       type: fieldType,
//       label: fieldConfig.label,
//       props: { ...fieldConfig.defaultProps },
//       roles: []
//     };
    
//     setSchema(prev => ({
//       ...prev,
//       fields: [...prev.fields, newField]
//     }));
//   };

//   const deleteField = (fieldId) => {
//     setSchema(prev => ({
//       ...prev,
//       fields: prev.fields.filter(field => field.id !== fieldId)
//     }));
//   };

//   const editField = (field) => {
//     setEditingField(field);
//     setEditorVisible(true);
//   };

//   const cloneField = (field) => {
//     const clonedField = {
//       ...field,
//       id: `field_${Date.now()}`,
//       label: `${field.label} (Copy)`
//     };
    
//     setSchema(prev => ({
//       ...prev,
//       fields: [...prev.fields, clonedField]
//     }));
//   };

//   const saveField = (updatedField) => {
//     setSchema(prev => ({
//       ...prev,
//       fields: prev.fields.map(field => 
//         field.id === updatedField.id ? updatedField : field
//       )
//     }));
//     setEditorVisible(false);
//     setEditingField(null);
//   };

//   const exportSchema = () => {
//     const blob = new Blob([JSON.stringify(schema, null, 2)], { type: 'application/json' });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `${schema.name.replace(/\s+/g, '_')}_schema.json`;
//     a.click();
//     URL.revokeObjectURL(url);
//   };

//   const importSchema = (file) => {
//     const reader = new FileReader();
//     reader.onload = (e) => {
//       try {
//         const importedSchema = JSON.parse(e.target.result);
//         setSchema(importedSchema);
//         message.success('Schema imported successfully!');
//       } catch (error) {
//         message.error('Invalid schema file!');
//       }
//     };
//     reader.readAsText(file);
//   };

//   const tabItems = [
//     {
//       key: 'builder',
//       label: (
//         <span>
//           <EditOutlined />
//           Form Builder
//         </span>
//       ),
//       children: (
//         <Layout style={{ minHeight: '600px' }}>
//           <Sider width={300} theme="light" className="border-r">
//             <div className="p-4">
//               <Title level={4}>Form Fields</Title>
//               <div className="space-y-2">
//                 {Object.entries(FIELD_TYPES).map(([key, config]) => (
//                   <DraggableField key={key} fieldType={key} onDrag={() => {}} />
//                 ))}
//               </div>
              
//               <Divider />
              
//               <div className="space-y-2">
//                 <Button 
//                   type="primary" 
//                   icon={<SaveOutlined />} 
//                   className="w-full"
//                   onClick={() => console.log('Schema saved:', schema)}
//                 >
//                   Save Form
//                 </Button>
//                 <Button 
//                   icon={<DownloadOutlined />} 
//                   className="w-full"
//                   onClick={exportSchema}
//                 >
//                   Export Schema
//                 </Button>
//                 <Upload
//                   accept=".json"
//                   showUploadList={false}
//                   beforeUpload={(file) => {
//                     importSchema(file);
//                     return false;
//                   }}
//                 >
//                   <Button icon={<UploadOutlined />} className="w-full">
//                     Import Schema
//                   </Button>
//                 </Upload>
//               </div>
//             </div>
//           </Sider>
          
//           <Content>
//             <div className="p-6">
//               <div className="mb-4 flex justify-between items-center">
//                 <div>
//                   <Title level={3}>{schema.name}</Title>
//                   <Text type="secondary">Drag fields from the sidebar to build your form</Text>
//                 </div>
//                 <div className="text-right">
//                   <Text type="secondary">Fields: {schema.fields.length}</Text>
//                 </div>
//               </div>
              
//               <div
//                 ref={dropZoneRef}
//                 className="min-h-96 border-2 border-dashed border-gray-300 rounded-lg p-4"
//                 onDragOver={handleDragOver}
//                 onDrop={handleDrop}
//               >
//                 {schema.fields.length === 0 ? (
//                   <div className="text-center py-20 text-gray-400">
//                     <DragOutlined className="text-4xl mb-4" />
//                     <p>Drag and drop fields here to build your form</p>
//                   </div>
//                 ) : (
//                   schema.fields.map(field => (
//                     <FormField
//                       key={field.id}
//                       field={field}
//                       onEdit={editField}
//                       onDelete={deleteField}
//                       onClone={cloneField}
//                     />
//                   ))
//                 )}
//               </div>
//             </div>
//           </Content>
//         </Layout>
//       )
//     },
//     {
//       key: 'preview',
//       label: (
//         <span>
//           <EyeOutlined />
//           Form Preview
//         </span>
//       ),
//       children: (
//         <div>
//           <div className="p-4 border-b bg-gray-50">
//             <div className="flex items-center justify-between">
//               <Title level={4} className="mb-0">Form Preview</Title>
//               <div className="flex items-center space-x-2">
//                 <Text>View as:</Text>
//                 <Select
//                   value={selectedRole}
//                   onChange={setSelectedRole}
//                   style={{ width: 120 }}
//                 >
//                   {Object.entries(EVENT_ROLES).map(([key, role]) => (
//                     <Option key={key} value={key}>{role.label}</Option>
//                   ))}
//                 </Select>
//               </div>
//             </div>
//           </div>
          
//           {schema.fields.length === 0 ? (
//             <div className="text-center py-20 text-gray-400">
//               <EyeOutlined className="text-4xl mb-4" />
//               <p>No fields to preview. Add some fields in the Form Builder tab.</p>
//             </div>
//           ) : (
//             <FormRenderer schema={schema} selectedRole={selectedRole} />
//           )}
//         </div>
//       )
//     }
//   ];

//   return (
//     <div className="h-screen bg-gray-50">
//       <div className="bg-white border-b px-6 py-4">
//         <Title level={2} className="mb-0">Event Form Builder</Title>
//         <Text type="secondary">Create dynamic registration forms for your events</Text>
//       </div>
      
//       <Tabs
//         activeKey={activeTab}
//         onChange={setActiveTab}
//         items={tabItems}
//         className="h-full"
//       />
      
//       <FieldEditor
//         visible={editorVisible}
//         field={editingField}
//         onSave={saveField}
//         onCancel={() => {
//           setEditorVisible(false);
//           setEditingField(null);
//         }}
//       />
//     </div>
//   );
// };

// export default FormList;